/**
 * A Google Maps directions link to a file's property, for the desk portal.
 *
 * Opened in a new tab; the officer's position is never read here and nothing
 * about the trip is recorded. Visit check-ins (from the phone) are the record.
 */
export function googleDirectionsUrl(target: {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
}): string | null {
  const hasCoords = typeof target.latitude === 'number' && typeof target.longitude === 'number';
  // The geocoded coordinate wins: it is where the file was placed, and an
  // address re-geocoded by Google can land on a neighbouring rural lot.
  const destination = hasCoords
    ? `${target.latitude},${target.longitude}`
    : (target.address?.trim() ?? '');
  if (!destination) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;
}

/** Google Maps takes at most this many intermediate stops in a directions link. */
export const GOOGLE_MAX_WAYPOINTS = 9;

/**
 * A Google Maps directions link through a day's remaining stops, in order:
 * the first is where the officer is going next, the last the destination, and
 * the ones between are waypoints — as many as Google takes; the rest of the
 * day is opened from the next stop on. The origin is left to Google, which
 * asks the device for it, so the portal never reads the officer's position.
 */
export function googleRouteUrl(
  stops: ReadonlyArray<{
    latitude?: number | null;
    longitude?: number | null;
    address?: string | null;
  }>,
): string | null {
  const points = stops
    .map((s) =>
      typeof s.latitude === 'number' && typeof s.longitude === 'number'
        ? `${s.latitude},${s.longitude}`
        : (s.address?.trim() ?? ''),
    )
    .filter(Boolean)
    .slice(0, GOOGLE_MAX_WAYPOINTS + 1);
  if (!points.length) return null;
  const destination = points[points.length - 1] as string;
  const waypoints = points.slice(0, -1);
  let url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  if (waypoints.length) url += `&waypoints=${encodeURIComponent(waypoints.join('|'))}`;
  return `${url}&travelmode=driving`;
}
